import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridApi, GridReadyEvent, GetRowIdFunc, CellKeyDownEvent } from 'ag-grid-community';
import dayjs from 'dayjs';

import { ExtendedRequisitionListResultItem } from '@customTypes/requisition';
import useBreakpoint from '@hooks/useBreakpoint';
import { getRequisitionTableColumns, defaultColumnDef } from './columns';
import { RequisitionTableDataSource } from './data-source';
import { RequisitionsErrorOverlay } from './RequisitionsErrorOverlay';
import { RequisitionsLoadingOverlay } from './RequisitionsLoadingOverlay';
import './RequisitionTable.scss';

interface RequisitionTableProps {
  exportCsvTrigger?: number;
  pageSize?: number;
  showPatientName?: boolean;
}

export const DEFAULT_PAGE_SIZE = 25;

/**
 * Returns a unique string ID to identify a given row to optimize data changes and updates.
 */
export const getRowId: GetRowIdFunc<ExtendedRequisitionListResultItem> = params => params.data.requisition_id;

/**
 * Click the link in the cell when pressing enter or space
 */
const onCellKeyDown = (gridEvent: CellKeyDownEvent): void => {
  const { key, target } = gridEvent.event as KeyboardEvent;

  if (key === 'Enter' || key === ' ') {
    (target as HTMLElement).querySelector('a')?.click();
  }
};

export const RequisitionTable: React.FC<RequisitionTableProps> = ({
  exportCsvTrigger,
  pageSize = DEFAULT_PAGE_SIZE,
  showPatientName = true,
}) => {
  const breakpoint = useBreakpoint();

  const [gridApi, setGridApi] = useState<GridApi<ExtendedRequisitionListResultItem> | null>(null);
  const [loadingError, setLoadingError] = useState<string | null>(null);

  const columnDefinitions = useMemo<ColDef<ExtendedRequisitionListResultItem>[]>(() => {
    const { clinician, download, externalMrn, orderDate, patient, requisitionId, result, sampleReceivedAt, status } =
      getRequisitionTableColumns({ breakpoint, showPatientName });

    return [download, patient, requisitionId, status, result, orderDate, sampleReceivedAt, clinician, externalMrn];
  }, [breakpoint, showPatientName]);

  const initDataSource = useCallback(
    (api: GridApi) => {
      setLoadingError(null);
      const dataSource = new RequisitionTableDataSource(api, pageSize, setLoadingError);
      api.setGridOption('datasource', dataSource);
    },
    [pageSize],
  );

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      setGridApi(event.api);
      initDataSource(event.api);
    },
    [initDataSource],
  );

  const handleRetry = useCallback(() => {
    if (gridApi) {
      initDataSource(gridApi);
    }
  }, [gridApi, initDataSource]);

  // Handle model updates (data, filters, sorting) to update overlay visibility
  // This fires after AG Grid has finished processing all updates
  const onModelUpdated = useCallback(() => {
    if (!gridApi) {
      return;
    }

    const displayedRowCount = gridApi.getDisplayedRowCount();

    if (displayedRowCount === 0) {
      gridApi.showNoRowsOverlay();
    } else {
      gridApi.hideOverlay();
    }
  }, [gridApi]);

  const handleExportCsv = useCallback(() => {
    if (!gridApi) {
      return;
    }

    gridApi.exportDataAsCsv({
      fileName: `orders-${dayjs().format('YYYY-MM-DD')}.csv`,
      columnSeparator: ',',
      suppressQuotes: false,
      columnKeys: [
        'patient',
        'sample_received_at',
        'requisition_id',
        'status',
        'result',
        'order_date',
        'clinician',
        'external_mrn',
      ],
      processCellCallback: params => {
        const colId = params.column.getColId();

        if (colId === 'patient' && !showPatientName) {
          return 'Hidden';
        }

        return params.formatValue(params.value);
      },
    });
  }, [gridApi, showPatientName]);

  // TODO: Move table interaction triggers into this component
  // The button to trigger an export currently exists outside this component
  // We receive a `exportCsvTrigger` prop when the export has been triggered
  // The prop is an incrementing integer, which prevents us from thinking
  // we always need to export
  useEffect(() => {
    if (exportCsvTrigger && exportCsvTrigger > 0) {
      handleExportCsv();
    }
  }, [exportCsvTrigger, handleExportCsv]);
  return (
    <div className='requisitions-table-container ag-theme-quartz' data-testid='requisitionsTable'>
      {loadingError ? (
        <RequisitionsErrorOverlay error={loadingError} onRetry={handleRetry} />
      ) : (
        <AgGridReact<ExtendedRequisitionListResultItem>
          cacheBlockSize={pageSize}
          columnDefs={columnDefinitions}
          defaultColDef={defaultColumnDef}
          enableCellTextSelection
          ensureDomOrder
          getRowId={getRowId}
          infiniteInitialRowCount={0}
          loadingOverlayComponent={RequisitionsLoadingOverlay}
          maxConcurrentDatasourceRequests={1}
          onCellKeyDown={onCellKeyDown}
          onGridReady={onGridReady}
          onModelUpdated={onModelUpdated}
          overlayNoRowsTemplate='No results found'
          pagination
          paginationPageSize={pageSize}
          paginationPageSizeSelector={false}
          reactiveCustomComponents // Defaults to true in v32+
          rowModelType='infinite'
          suppressDragLeaveHidesColumns
          tooltipHideDelay={2000}
          tooltipShowDelay={0}
        />
      )}
    </div>
  );
};
